import { useAlertContext } from "@shared";
import { useQueryClient } from "@tanstack/react-query";

import { documentsApi } from "@/api";
import { withLocalErrorHandling } from "@/api/middleware/errorMiddleware";
import { PendingDocumentMove } from "@/common/components/layout/components/dropzone/dnd/DropZoneDnd.types";
import { invalidateDocumentZoneQueries } from "@/common/components/layout/components/dropzone/dnd/DropZoneDnd.utils";
import { useTranslation } from "@/translations";

/**
 * Persists a view's pending document moves on save — shared by the owning views (Persönliche
 * Daten, Angehörige) and the documents overview. Every move is executed independently, failures
 * are reported per file and returned so the caller keeps them pending in its form, and every
 * document list a cross-zone move can affect is refreshed.
 */
export function useAssetInventoryMoves(clientId: string) {
  const { t } = useTranslation();
  const { showError } = useAlertContext();
  const queryClient = useQueryClient();

  return async (moves: PendingDocumentMove[]): Promise<PendingDocumentMove[]> => {
    if (moves.length === 0) {
      return [];
    }

    const failed: PendingDocumentMove[] = [];
    await Promise.all(
      moves.map(async (move) => {
        try {
          await documentsApi.moveDocument(
            {
              clientId,
              moveDocumentRequest: { target: move.target, fileId: move.document.documentId },
            },
            withLocalErrorHandling
          );
        } catch {
          failed.push(move);
          showError(t("dropzone.dnd.moveFileError", { fileName: move.document.fileName }));
        }
      })
    );

    invalidateDocumentZoneQueries(queryClient, clientId);
    return failed;
  };
}
